
import { Component, Vue, Watch } from "vue-property-decorator";

interface HelpAction {
  label: string;
  routeName?: string;
  prompt?: string;
}

interface HelpMessage {
  id: string;
  role: "bot" | "user";
  text: string;
  actions: HelpAction[];
}

interface RouteGuide {
  routeName: string;
  title: string;
  guidance: string;
  steps: string[];
  keywords: string[];
}

interface BotReply {
  text: string;
  actions: HelpAction[];
}

interface CloudHelpResponse {
  reply?: string;
  routeName?: string;
  suggestions?: string[];
  error?: string;
}

type SsePayload = CloudHelpResponse & {
  text?: string;
};

interface QuickPrompt {
  label: string;
  query: string;
}

const ROUTE_GUIDES: RouteGuide[] = [
  {
    routeName: "home",
    title: "Main Menu",
    guidance: "Use Main Menu as your launch point for all day-to-day workflows.",
    steps: [
      "Choose Crib Scores to capture bunk readings.",
      "Choose Feed Planning to edit ration plans and kraal details.",
      "Choose Options/Settings for system configuration changes.",
    ],
    keywords: ["home", "main menu", "dashboard", "start", "menu"],
  },
  {
    routeName: "location",
    title: "Location",
    guidance: "Use Location to select the active feedlot before working in the app.",
    steps: ["Open Location.", "Select the required feedlot row.", "Press Select to continue to the main menu."],
    keywords: ["location", "switch site", "change site", "select feedlot", "choose location"],
  },
  {
    routeName: "crib",
    title: "Crib Scores",
    guidance: "Crib Scores is where you capture and update feeding score data by kraal.",
    steps: [
      "Open Crib Scores and search for the target kraal.",
      "Select or edit the score values and confirm changes.",
      "Check status icons for successful upload or retry failed rows.",
    ],
    keywords: ["crib", "crib score", "bunk score", "feed score", "scores"],
  },
  {
    routeName: "plan",
    title: "Feed Planning",
    guidance: "Feed Planning is used to manage kraal plans, ration progress, and key lot details.",
    steps: [
      "Open Feed Planning and filter/search the target kraal.",
      "Select a kraal row to unlock actions like Edit, Move, and Clear.",
      "Save edits and watch the row status icon for upload confirmation.",
    ],
    keywords: ["feed plan", "planning", "ration plan", "kraal plan", "plan"],
  },
  {
    routeName: "mix",
    title: "Mix Schedule",
    guidance: "Mix Schedule helps you monitor and manage planned mixing operations.",
    steps: ["Open Mix Schedule.", "Review mix sessions and selected ration data.", "Adjust or refresh as needed for the next run."],
    keywords: ["mix", "mix schedule", "mixer", "mixing"],
  },
  {
    routeName: "schedule",
    title: "Feeding Schedule",
    guidance: "Feeding Schedule shows upcoming feed jobs and sequencing details.",
    steps: [
      "Open Feeding Schedule.",
      "Review schedule rows for timing, trailer, and ration details.",
      "Use available actions to pause/resume or refresh where permitted.",
    ],
    keywords: ["schedule", "feeding schedule", "jobs", "feed jobs"],
  },
  {
    routeName: "history",
    title: "History",
    guidance: "History lets you review daily crib, feeding schedule, and plan data together for each kraal.",
    steps: [
      "Open History.",
      "Choose a kraal and date range if you want to narrow the data.",
      "Select a row to inspect the lower breakdown table for that day.",
    ],
    keywords: ["history", "feed history", "feeding history", "crib history", "historical"],
  },
  {
    routeName: "delivery",
    title: "Feed Delivery",
    guidance: "Feed Delivery provides a view of delivered feed entries and related details.",
    steps: ["Open Feed Delivery.", "Search/filter by trailer, kraal, or operator.", "Open rows to inspect delivery specifics."],
    keywords: ["delivery", "feed delivery", "deliveries", "dropped feed"],
  },
  {
    routeName: "move",
    title: "Animal Movements",
    guidance: "Animal Movements is used for transferring stock between kraals.",
    steps: ["Open Animal Movements.", "Choose source and destination kraals.", "Enter quantities and save to apply movements."],
    keywords: ["animal movement", "move animals", "transfer", "animal"],
  },
  {
    routeName: "setup",
    title: "Farm Setup",
    guidance: "Farm Setup manages core entities like kraals, trailers, tractors, and operators.",
    steps: ["Open Farm Setup.", "Select the entity list you need to maintain.", "Create or edit records and save changes."],
    keywords: ["setup", "farm setup", "master data", "entities"],
  },
  {
    routeName: "options",
    title: "Options/Settings",
    guidance: "Options/Settings controls strategy, sequencing, adjustments, and administrative config.",
    steps: ["Open Options/Settings.", "Select the relevant tab for the setting you want to change.", "Edit values and save to update server configuration."],
    keywords: ["options", "settings", "configuration", "admin", "strategy"],
  },
  {
    routeName: "map",
    title: "Map",
    guidance: "Map helps you view location and layout context for operational planning.",
    steps: ["Open Map.", "Inspect the feedlot layout and references.", "Use it to verify route/location context."],
    keywords: ["map", "location map", "layout"],
  },
  {
    routeName: "notifications",
    title: "Notifications",
    guidance: "Notifications lists active operational alerts and message details.",
    steps: ["Open Notifications.", "Review active alerts and conditions.", "Acknowledge or act based on the message context."],
    keywords: ["notification", "notifications", "alerts", "messages"],
  },
  {
    routeName: "outbox",
    title: "Outbox",
    guidance: "Outbox is used to monitor and retry unsent offline records.",
    steps: ["Open Outbox.", "Review pending items and failed uploads.", "Use Upload to retry sending queued records."],
    keywords: ["outbox", "offline", "sync", "upload queue", "pending"],
  },
];

const QUICK_PROMPTS: QuickPrompt[] = [
  { label: "How to use Feed Planning", query: "How do I use feed planning?" },
  { label: "Where do I change settings?", query: "Where do I change settings?" },
  { label: "Help with notifications", query: "How do notifications work?" },
  { label: "Open Main Menu", query: "Open main menu" },
];

@Component
export default class HelpBot extends Vue {
  isOpen = false;
  draft = "";
  loading = false;
  messageCounter = 0;
  messages: HelpMessage[] = [];
  quickPrompts = QUICK_PROMPTS;

  mounted() {
    const welcome = this.createWelcomeReply();
    this.pushBot(welcome.text, welcome.actions);
  }

  @Watch("isOpen")
  onOpenChanged(nextIsOpen: boolean) {
    if (nextIsOpen) {
      this.$nextTick(() => this.scrollToBottom());
    }
  }

  get canSend() {
    return this.draft.trim().length > 0 && !this.loading;
  }

  toggleOpen() {
    this.isOpen = !this.isOpen;
  }

  async sendMessage() {
    const userInput = this.draft.trim();
    if (!userInput || this.loading) {
      return;
    }

    this.draft = "";
    this.pushUser(userInput);
    const botMessageId = this.pushBot("Thinking...", []);
    this.loading = true;

    try {
      const cloudReply = await this.tryCloudHelp(userInput, botMessageId);
      const reply = cloudReply ?? this.buildLocalReply(userInput);
      this.setBotMessage(botMessageId, reply.text, reply.actions);
    } finally {
      this.loading = false;
    }
  }

  sendQuickPrompt(prompt: string) {
    if (this.loading) {
      return;
    }
    this.draft = prompt;
    this.sendMessage();
  }

  onAction(action: HelpAction) {
    if (action.prompt) {
      this.sendQuickPrompt(action.prompt);
      return;
    }

    if (action.routeName) {
      this.navigateTo(action.routeName);
      this.pushBot(`Opening ${this.getRouteTitle(action.routeName)}.`, []);
    }
  }

  private createWelcomeReply(): BotReply {
    return {
      text: "I can help you find screens and explain workflows.\n" + 'Try: "Open feed planning", "How do I use crib scores?", or "Where do I change settings?"',
      actions: [
        { label: "Open Feed Planning", routeName: "plan" },
        { label: "Open Feeding Schedule", routeName: "schedule" },
        { label: "Open Options", routeName: "options" },
      ],
    };
  }

  private normalize(input: string) {
    return input
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  private buildLocalReply(userInput: string): BotReply {
    const normalized = this.normalize(userInput);
    const currentRouteName = String(this.$route.name || "");
    const currentGuide = ROUTE_GUIDES.find((guide) => guide.routeName === currentRouteName);
    const wantsNavigation = /(go to|open|navigate|take me|show me)/.test(normalized);
    const wantsSteps = /(how|steps|use|workflow|process)/.test(normalized);

    if (/(where am i|current page|current screen|what page)/.test(normalized)) {
      if (currentGuide) {
        return {
          text: `You are on ${currentGuide.title}. ${currentGuide.guidance}`,
          actions: [{ label: `Open ${currentGuide.title}`, routeName: currentGuide.routeName }],
        };
      }
      return {
        text: "I could not determine the current screen name, but I can still help you navigate.",
        actions: [{ label: "Open Main Menu", routeName: "home" }],
      };
    }

    if (/(log out|logout|sign out)/.test(normalized)) {
      return {
        text: "Use the top-right menu (three dots) and select Log Out.",
        actions: [],
      };
    }

    if (/(change location|switch location|different location|select location)/.test(normalized)) {
      return {
        text: "To switch feedlots, open the Location screen, select the row, and press Select.",
        actions: [{ label: "Open Location", routeName: "location" }],
      };
    }

    const matchedGuide = this.findBestGuide(normalized);
    if (matchedGuide) {
      const lines: string[] = [`${matchedGuide.title}: ${matchedGuide.guidance}`];
      if (wantsSteps || !wantsNavigation) {
        matchedGuide.steps.forEach((step, index) => lines.push(`${index + 1}. ${step}`));
      }
      const actions: HelpAction[] = [{ label: `Open ${matchedGuide.title}`, routeName: matchedGuide.routeName }];
      if (matchedGuide.routeName !== "home") {
        actions.push({ label: "Open Main Menu", routeName: "home" });
      }
      return {
        text: lines.join("\n"),
        actions,
      };
    }

    if (normalized.length <= 8 || /(help|what can you do|commands)/.test(normalized)) {
      return this.createWelcomeReply();
    }

    return {
      text:
        `I could not match that request yet. ${currentGuide ? `You are on ${currentGuide.title}. ` : ""}` +
        'Ask me things like "How do I use Feed Planning?" or "Open Notifications".',
      actions: [
        { label: "Open Feed Planning", routeName: "plan" },
        { label: "Open Notifications", routeName: "notifications" },
        { label: "Open Options", routeName: "options" },
      ],
    };
  }

  private findBestGuide(normalizedInput: string) {
    let bestGuide: RouteGuide | undefined;
    let bestScore = 0;

    for (const guide of ROUTE_GUIDES) {
      let score = 0;
      for (const keyword of guide.keywords) {
        const normalizedKeyword = this.normalize(keyword);
        if (!normalizedKeyword) {
          continue;
        }
        if (normalizedInput.includes(normalizedKeyword)) {
          score += normalizedKeyword.includes(" ") ? 3 : 1;
        }
      }
      if (score > bestScore) {
        bestScore = score;
        bestGuide = guide;
      }
    }

    return bestScore > 0 ? bestGuide : undefined;
  }

  private async tryCloudHelp(userInput: string, botMessageId: string): Promise<BotReply | undefined> {
    const endpoint = this.getHelpbotEndpoint();
    if (!endpoint) {
      return undefined;
    }

    try {
      const jwt = await this.$store.dispatch("user/getOnlineIdToken", undefined, { root: true });
      const payload = {
        prompt: userInput,
        currentRoute: String(this.$route.name || "home"),
        locationGuid: this.$store.state?.user?.location?.guid ?? "",
        history: this.messages
          .filter((message) => message.id !== botMessageId)
          .slice(-6)
          .map((message) => ({ role: message.role, text: message.text })),
        stream: true,
      };
      const response = await fetch(endpoint, {
        method: "POST",
        headers: new Headers({
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
          Accept: "text/event-stream, application/json",
        }),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        return undefined;
      }

      const contentType = (response.headers.get("content-type") || "").toLowerCase();
      if (contentType.includes("text/event-stream") && response.body) {
        return await this.consumeSseResponse(response, botMessageId);
      }

      const data = (await response.json()) as CloudHelpResponse;
      if (!data.reply) {
        return undefined;
      }

      return {
        text: data.reply,
        actions: this.getCloudActions(data),
      };
    } catch (error) {
      console.warn("HelpBot cloud endpoint failed, falling back to local guidance.", error);
      return undefined;
    }
  }

  private getHelpbotEndpoint(): string {
    const environment = this.$store.state.environment as "production" | "staging" | "testing" | "undetermined";
    const nonProdEndpoint = (process.env.VUE_APP_HELPBOT_ENDPOINT || "https://helpbot-dvk7ycog5a-ew.a.run.app").trim();

    if (environment === "production") {
      return "https://europe-west1-gigalot-cloud.cloudfunctions.net/helpbot";
    }

    return nonProdEndpoint;
  }

  private async consumeSseResponse(response: Response, botMessageId: string): Promise<BotReply | undefined> {
    if (!response.body) {
      return undefined;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let streamedText = "";
    let seenChunk = false;
    let finalPayload: CloudHelpResponse | undefined;

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");

      let delimiterIndex = buffer.indexOf("\n\n");
      while (delimiterIndex >= 0) {
        const frame = buffer.slice(0, delimiterIndex);
        buffer = buffer.slice(delimiterIndex + 2);
        delimiterIndex = buffer.indexOf("\n\n");

        const event = this.parseSseFrame(frame);
        if (!event) {
          continue;
        }

        if (event.name === "chunk") {
          const textChunk = typeof event.data?.text === "string" ? event.data.text : "";
          if (!textChunk) {
            continue;
          }
          streamedText += textChunk;
          if (!seenChunk) {
            this.setBotMessage(botMessageId, textChunk, []);
            seenChunk = true;
          } else {
            this.appendToBotMessage(botMessageId, textChunk);
          }
        } else if (event.name === "done") {
          finalPayload = event.data;
        } else if (event.name === "error") {
          return undefined;
        }
      }
    }

    if (finalPayload?.reply) {
      return {
        text: finalPayload.reply,
        actions: this.getCloudActions(finalPayload),
      };
    }

    if (streamedText.trim()) {
      return {
        text: streamedText.trim(),
        actions: [],
      };
    }

    return undefined;
  }

  private parseSseFrame(frame: string): { name: string; data: SsePayload } | undefined {
    const lines = frame.split("\n");
    let name = "message";
    const dataLines: string[] = [];

    for (const line of lines) {
      if (line.startsWith("event:")) {
        name = line.slice("event:".length).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice("data:".length).trim());
      }
    }

    if (!dataLines.length) {
      return undefined;
    }

    const dataRaw = dataLines.join("\n");
    try {
      return {
        name,
        data: JSON.parse(dataRaw),
      };
    } catch {
      return {
        name,
        data: { text: dataRaw },
      };
    }
  }

  private getCloudActions(data: CloudHelpResponse): HelpAction[] {
    const actions: HelpAction[] = [];
    if (data.routeName) {
      actions.push({
        label: `Open ${this.getRouteTitle(data.routeName)}`,
        routeName: data.routeName,
      });
    }
    for (const suggestion of data.suggestions ?? []) {
      actions.push({ label: suggestion, prompt: suggestion });
    }
    return actions.slice(0, 3);
  }

  private getRouteTitle(routeName: string) {
    return ROUTE_GUIDES.find((guide) => guide.routeName === routeName)?.title ?? routeName;
  }

  private navigateTo(routeName: string) {
    if (this.$route.name === routeName) {
      return;
    }
    this.$router.push({ name: routeName }).catch((error: any) => {
      if (!error || error.name !== "NavigationDuplicated") {
        console.error(error);
      }
    });
  }

  private setBotMessage(messageId: string, text: string, actions: HelpAction[]) {
    const message = this.messages.find((item) => item.id === messageId);
    if (!message) {
      return;
    }
    message.text = text;
    message.actions = actions;
    this.$nextTick(() => this.scrollToBottom());
  }

  private appendToBotMessage(messageId: string, textChunk: string) {
    const message = this.messages.find((item) => item.id === messageId);
    if (!message) {
      return;
    }
    message.text += textChunk;
    this.$nextTick(() => this.scrollToBottom());
  }

  private pushBot(text: string, actions: HelpAction[]) {
    const message: HelpMessage = {
      id: this.nextMessageId(),
      role: "bot",
      text,
      actions,
    };
    this.messages.push(message);
    this.$nextTick(() => this.scrollToBottom());
    return message.id;
  }

  private pushUser(text: string) {
    this.messages.push({
      id: this.nextMessageId(),
      role: "user",
      text,
      actions: [],
    });
    this.$nextTick(() => this.scrollToBottom());
  }

  private nextMessageId() {
    this.messageCounter += 1;
    return `helpbot-${Date.now()}-${this.messageCounter}`;
  }

  private scrollToBottom() {
    const container = this.$refs.messagesContainer as HTMLElement | undefined;
    if (!container) {
      return;
    }
    container.scrollTop = container.scrollHeight;
  }
}
